import { useLingui } from '@lingui/react/macro';
import { authStoreUserTournamentId } from 'modules/auth/store/selectors';
import { useUsersPagesCache } from 'modules/roles-manager/ui/hooks/use-roles-manager-provider';
import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useLocalStorage } from 'shared/hooks/use-local-storage';
import { useQueryParams } from 'shared/hooks/use-query-params';
import { debounce } from 'shared/utils';
import { deriveAsyncState } from 'shared/utils/async-state';
import useRoleUsers from './use-role-users';

function useUsersList() {
  const { t } = useLingui();
  const userTournamentId = useSelector(authStoreUserTournamentId);

  const { setPageCount, setPageData, pagesCache, pageCount, resetPagesCache } = useUsersPagesCache();

  const [params, setParams] = useQueryParams({
    page: { key: 'page', defaultValue: 1, parser: Number },
    status: { key: 'status', defaultValue: null, parser: Number },
    name: { key: 'name', defaultValue: null, parser: String },
    phone: { key: 'phone', defaultValue: null, parser: String },
  });

  const [storage, setStorage] = useLocalStorage({
    perPage: { key: 'usersPerPage', defaultValue: 8, parser: Number },
  });

  const { data, currentData, isFetching, isError, refetch } = useRoleUsers({
    tournamentId: userTournamentId ?? undefined,
    page: params.page || 1,
    perPage: storage.perPage || 8,
    filters: {
      isActive: params.status ?? undefined,
      name: params.name ?? undefined,
      phone: params.phone ?? undefined,
    },
  });

  const debouncedSetPerPage = useMemo(
    () =>
      debounce((value: number) => {
        setParams({ page: 1 });
        setStorage({ perPage: Number(value) });
        resetPagesCache();
      }, 500),
    [resetPagesCache, setParams, setStorage],
  );

  const debouncedSetName = useMemo(
    () =>
      debounce((value: string) => {
        if (value && value !== '') {
          setParams({ name: value, page: 1 });
        } else {
          setParams({ name: null, page: 1 });
        }
        resetPagesCache();
      }, 500),
    [resetPagesCache, setParams],
  );

  const debouncedSetPhone = useMemo(
    () =>
      debounce((value: string) => {
        if (value && value !== '') {
          setParams({ phone: value, page: 1 });
        } else {
          setParams({ phone: null, page: 1 });
        }
        resetPagesCache();
      }, 500),
    [resetPagesCache, setParams],
  );

  const changeFilter = useCallback(
    (key: keyof typeof params | keyof typeof storage, value: any) => {
      switch (key) {
        case 'page':
          setParams({ page: Number(value) });
          break;
        case 'perPage':
          debouncedSetPerPage(value);
          break;
        case 'status':
          if (value !== null) {
            setParams({ status: Number(value), page: 1 });
          } else {
            setParams({ status: null, page: 1 });
          }
          resetPagesCache();
          break;
        case 'name':
          debouncedSetName(value);
          break;
        case 'phone':
          debouncedSetPhone(value);
          break;
        default:
          toast.error(t`Неизвестный фильтр`);
      }
    },
    [debouncedSetName, debouncedSetPerPage, resetPagesCache, setParams, t],
  );

  // `currentData`: после ошибки `data` ещё держит прошлую страницу, и её нельзя записать под текущим номером.
  useEffect(() => {
    if (currentData && !isFetching) {
      setPageData(params.page, currentData);

      const maxPage = Math.max(...Object.keys(pagesCache).map(Number));
      const lastPageData = pagesCache[maxPage];
      const lastPageIsEmpty = lastPageData?.length === 0;
      const hasMore = lastPageData?.length >= storage.perPage;

      let safeMaxPage: number = maxPage;
      if (hasMore) safeMaxPage = maxPage + 1;
      else if (lastPageIsEmpty) safeMaxPage = maxPage - 1;

      setPageCount(safeMaxPage);
      if (params.page > safeMaxPage) setParams({ page: safeMaxPage });
    }
  }, [currentData, isFetching, pagesCache, params.page, setPageCount, setPageData, setParams, storage.perPage]);

  /**
   * Четыре взаимоисключающие ветки вместо пары булевых — `.claude/docs/async-loading-states.md`.
   * RTK `data` держит ответ предыдущих аргументов, а «данные относятся к текущему запросу» — это
   * `currentData !== undefined`: иначе смена фильтра не отличалась бы от первой загрузки и список
   * схлопывался бы на каждом нажатии клавиши. После ошибки `currentData` пуст, поэтому ветка
   * `error` достижима и при прошлой странице в `data`.
   */
  const state = deriveAsyncState({
    itemsCount: currentData?.length ?? data?.length ?? 0,
    hasCurrentData: currentData !== undefined,
    isFetching,
    isError,
  });

  return {
    data: data ?? [],
    pages: pageCount,
    page: params.page,
    changeFilter,
    state,
    refetch,
    filters: {
      status: params.status,
      name: params.name,
      perPage: storage.perPage,
      phone: params.phone,
    },
  };
}

export default useUsersList;
